import { OverridableComponent } from "@material-ui/core/OverridableComponent";
import { SvgIconTypeMap } from "@material-ui/core";

import { AdGroupTemplate, AdGroupType } from "./adGroupTypes";
import { CreativeAssetToCreate } from "./creativeTypes";

// API request payload types (Frontend)

export type UpdatedCampaignBody = {
  name: string;
  description: string;
  dailyBudget: number;
  campaign_budget: number;
  maxBid: number;
  startDate: Date;
  endDate: Date;
  active: boolean;
  maxCompanies: number;
  showInHubspot: boolean;
  isVideoCampaign: boolean;
  archived: boolean;
};

export type ArchiveCampaignBody = {
  archived: boolean;
};

export type StatusCampaignBody = {
  active: boolean;
};

export type BulkCampaignActionResult = {
  succeeded: string[];
  failed: { brandgenCampaignPoolId: string; error: string }[];
};

// API request payload types (Backend)

export type UpdatedCampaignPayload = {
  name: string;
  description: string;
  campaign_budget: number;
  max_bid: number;
  start_date: Date;
  end_date: Date;
  active: boolean;
  maxCompanies: number;
  showInHubspot: boolean;
  isVideoCampaign: boolean;
  archived: boolean;
};

export type CampaignLinteItemType = {
  active: boolean;
  fromFile: string | null;
  hubspotCompanyId: number;
  beeswaxAdvertiserId: number;
  beeswaxCampaignId: number;
  beeswaxLineItemId: number;
  brandgenCampaignId: string;
  createdAt: string;
  lineItemReports?: LineItemReportType[];
  line_item_budget: number;
  line_item_name: string;
  line_item_spend: number;
  segment_count_nam: number;
  updatedAt: string;
};

export type CamapignReportType = {
  brandgenCampaignReportId: string;
  brandgenCampaignId: string;
  timeFrame: string;
  beeswaxCampaignId: number;
  beeswaxAdvertiserId: number;
  clicks: number;
  cpc_usd: number;
  cpm_usd: number;
  ctr: number;
  impressions: number;
  spend: number;
  conversions: number;
  click_through_conversions: number;
  spend_per_conversion: number;
  viewthrough_conversions: number;
  video_companion_clicks: number;
  video_companion_ctr: number;
  video_companion_views: number;
  video_complete_percent: number;
  video_completes: number;
  video_midpoints: number;
  video_plays: number;
  video_q1: number;
  video_q3: number;
  video_skips: number;
  average_viewable_seconds: number;
  invalid_impression_percent: number;
  invalid_impressions: number;
  measurable_impression_percent: number;
  measurable_impressions: number;
  measurable_invalid_impressions: number;
  vcpm: number;
  viewability_percent: number;
  viewability_measurability_percent: number;
  viewable_impressions: number;
  viewable_seconds: number;
  conversion_orders: number;
  conversions_value: number;
  postback_conversions: number;
  reach: number;
};

export type LineItemReportType = {
  brandgenLineItemReportId: string;
  timeFrame: ReportTimeFrames;
  beeswaxLineItemId: number;
  beeswaxCampaignId: number;
  beeswaxAdvertiserId: number;
  clicks: number;
  cpc_usd: number;
  cpm_usd: number;
  ctr: number;
  impressions: number;
  spend: number;
  conversions: number;
  click_through_conversions: number;
  spend_per_conversion: number;
  viewthrough_conversions: number;
  inventory_cost: number;
  media_spend: number;
  gross_margin: number;
  gross_margin_percent: number;
  net_margin: number;
  net_margin_percent: number;
  revenue: number;
  video_companion_clicks: number;
  video_companion_ctr: number;
  video_companion_views: number;
  video_complete_percent: number;
  video_completes: number;
  video_midpoints: number;
  video_plays: number;
  video_q1: number;
  video_q3: number;
  video_skips: number;
  average_viewable_seconds: number;
  invalid_impression_percent: number;
  invalid_impressions: number;
  measurable_impression_percent: number;
  measurable_impressions: number;
  measurable_invalid_impressions: number;
  vcpm: number;
  viewability_percent: number;
  viewability_measurability_percent: number;
  viewable_impressions: number;
  viewable_seconds: number;
  conversion_orders: number;
  conversions_value: number;
  postback_conversions: number;
  reach: number;
};

export type CreativeReportType = {
  beeswaxCreativeId: number;
  brandgenCreativeReportId: string;
  timeFrame: ReportTimeFrames;
  beeswaxAdvertiserId: number;
  clicks: number;
  ctr: number;
  impressions: number;
  video_completes: number;
  video_midpoints: number;
  video_plays: number;
};

export type AdLibCreativeReportType = {
  id: string;
  adLibCreativeId: number;
  adLibAdvertiserId: number;
  timeFrame: string;
  campaignName?: string | null;
  creativeName?: string | null;
  currencyCode?: string | null;
  date?: string | null;
  impressions?: number | null;
  clicks?: number | null;
  videoViews?: number | null;
  videoComplete?: number | null;
  ctr?: number | null;
  cpm?: number | null;
  cpc?: number | null;
};

export type CampaignType = {
  brandgenCampaignId: string;
  beeswaxCampaignId: number;
  beeswaxAdvertiserId: number;
  createdBy: string;
  name: string;
  description: string;
  active: boolean;
  start_date: string;
  end_date: string;
  daily_budget: number;
  campaign_budget: number;
  pixel_event_id: number;
  burnRate: number;
  accounts: number;
  brandgenAdvertiserId: string;
  billed_amount: number;
  showInHubspot: boolean;
  targeting_info: TargetingInfo;
  creatives?: Array<number>;
  maxBid: number;
  isVideoCampaign: boolean;
  maxCompanies: number;
  archived: boolean;
  agencyId: string;
  campaignReports?: Array<CamapignReportType>;
  lineItems?: Array<any>;
};

export type BrandgenCampaignPoolReport = {
  id: string;
  brandgenCampaignPoolId: string;
  timeFrame: string;
  adLibAdvertiserId: number;
  campaignName?: string | null;
  currencyCode?: string | null;
  date?: string | null;
  impressions?: number | null;
  clicks?: number | null;
  videoViews?: number | null;
  videoComplete?: number | null;
  ctr?: number | null;
  cpm?: number | null;
  cpc?: number | null;
  cpv?: number | null;
  cpcv?: number | null;
  cpa?: number | null;
  totalConversions?: number | null;
  totalRevenue?: number | null;
  totalSpend?: number | null;
  nonBilledDataCost?: number | null;
  billedDataCost?: number | null;
  createdAt?: string;
  updatedAt?: string;
};

export type BrandgenCampaignPool = {
  brandgenCampaignPoolId: string;
  hubspotCampaignId?: string;
  brandgenAdvertiserId: string;
  adLibAdvertiserId: number;
  createdBy: string;
  updatedBy?: string;
  name: string;
  description: string;
  active: boolean;
  start_date: string;
  end_date: string;
  daily_budget: number;
  campaign_budget: number;
  maxBid: number;
  pixel_event_id: number;
  burnRate: number;
  showInHubspot: boolean;
  maxCompanies: number;
  isVideoCampaign: boolean;
  archived: boolean;
  accounts?: number;
  targeting_info?: TargetingInfo;
  creatives: number[];
  billed_amount: number;
  campaignType?: string | null;
  status?: "processing" | "active" | "error" | null;
  createdAt?: string;
  updatedAt?: string;
  campaignPoolReports?: Array<BrandgenCampaignPoolReport>;
};

export type CampaignListParams = {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDir?: "ASC" | "DESC";
  // Which report period metric sorting (spend/impressions/clicks/ctr) is applied to.
  sortTimeFrame?: string;
  search?: string;
  displayStatus?: string;
  campaignType?: string;
  startDateFrom?: string;
  endDateTo?: string;
};

export type CampaignListResponse = {
  data: BrandgenCampaignPool[];
  total: number;
  page: number;
  limit: number;
};

export type TargetingInfo = {
  domain_list: Array<number>;
  geo_country: Array<string>;
  geo_state: Array<string>;
  geo_zip: Array<string>;
  role_title_segments: Array<string>;
  retargeting_segments: Array<string>;
};

export type TargetingSelections = {
  domain_list: Array<BrandgenDomainList>;
  geo_country: Array<string>;
  geo_state: Array<GeoState>;
  geo_zip: Array<string>;
  role_title_segments: Array<Role>;
  retargeting_segments: Array<string>;
};

// export type GeoState = {
//   country_code_2: string;
//   country_code_3: string;
//   key: string;
//   region_code: string;
//   region_name: string;
// };

export type BrandgenDomainList = {
  domainListId: string;
  beeswaxListId: number;
  brandgenAdvertiserId: string;
  name: string;
  domains: Array<BrandgenDomainListItem>;
};

export type BrandgenDomainListItem = {
  active: boolean;
  beeswaxListId: number;
  beeswaxListItemId: number;
  brandgenItemId: string;
  createdAt: string;
  domain: string;
  domainListId: string;
  updatedAt: string;
};

export type RolesTitlesHolder = {
  role: string;
  cookieSets: TitlesHolder[];
};

export type TitlesHolder = {
  adLibId: number;
  title: string;
};

export type RoleType = {
  roleTypeId: string;
  role_type: string;
  createdAt: Date;
  updatedAt: Date;
  roles?: Role[];
};

export type Role = {
  roleId: string;
  role_name: string;
  roleTypeId: string;
  custom_order: number;
  createdAt: Date;
  updatedAt: Date;
  segments?: Segment[];
};

export type Segment = {
  brandgenSegmentId: string;
  roleId: string;
  segment_name: string;
  segment_key: string;
  // segment_id: string;
  // segment_description: string;
  // date_source_name: string;
  // idB2BData: boolean;
  // isABMData: boolean;
  // isIntentData: boolean;
  // isTVData: boolean;
  // isRoleBased: boolean;
  // isCompanyData: boolean;
  // isBuyerShopperData: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export enum ReportTimeFrames {
  ALL_TIME = "all_time",
  DAYS_7 = "7_days",
  DAYS_30 = "30_days",
  DAYS_90 = "90_days",
  CUSTOM_DATE = "custom_date",
}

export type AdGroupToCreateType = {
  name: string;
  description: string;
  adGroupType: AdGroupType;
  creativeAssets: CreativeAssetToCreate[];
};

export type SelectedAdGroupTemplate = {
  adGroup: AdGroupTemplate;
  creatives: CreativeAssetToCreate[];
};

export type TierValueType = {
  description: string;
  displayOrder: number;
  hidden: boolean;
  label: string;
  value: string;
};

export type CampaignCompanySortValuesType = {
  tier_values: TierValueType[];
};

export type HubSpotList = {
  listId: number;
  name: string;
  listType: string;
};

export type HubSpotListsType = {
  lists: HubSpotList[];
};

export type BrandgenCampaignTargetingType = {
  domains: string[][];
  segments?: string[];
  blogKeywords?: string[];
  blogTopics?: string[];
  geoRegions?: Array<any>;
};

export type BeeswaxCampaignCreateType = {
  advertiser_id: number;
  campaign_name: string;
  campaign_budget: number | string;
  budget_type: number | string;
  start_date: Date;
  end_date?: Date;
  active: boolean;
};

export type BrandgenCampaignCreateType = {
  beeswaxCampaign: BeeswaxCampaignCreateType;
  targetingInfo: BrandgenCampaignTargetingType;
  brandgenCampaignId: string;
  brandgenAdvertiserId: string;
  agencyId: string;
  createdBy: string;
  name: string;
  description: string;
  creative: number[];
};

export type CampaignBasicDetailsType = {
  brandgenCampaignId: string;
  agencyId: string;
  createdBy: string;
  name: string;
  description: string;
  startDate: Date | string;
  endDate: Date | string;
  estimatedBudget: number | string;
  dailyBudget: number | string;
  maxBid: number | string;
  showInHubspot: boolean;
  maxCompanies: number | string;
  isVideoCampaign: boolean;
};

export type FoundCompanyInfo = {
  domain: string;
  domainInfo: boolean;
  include: boolean;
  hubspotCompanyId?: string;
};

export type FoundCompaniesInfo = {
  uniqueSegments: Array<FoundCompanyInfo> | undefined;
  primarySegments: Array<FoundCompanyInfo> | undefined;
  matches: number;
  fromFile: string | null;
};

export type FoundHubspotPages = {
  pages: Array<any>;
  blogPosts: Array<any>;
};

export type FoundHubspotPage = {
  id: number;
  name: string;
  url: string;
  pageType: "BLOG" | "LANDING" | "WEB";
  state: string;
};

export type CookieSet = {
  adLibId: number;
  title: string;
};

export type RoleAndTitlesSet = {
  id: string;
  name: string;
  brandgenAdvertiserId: string;
  rolesAndTitlesAdLibIds: number[];
  createdAt?: string;
  updatedAt?: string;
};

export type StateProvinceSet = {
  id: string;
  name: string;
  brandgenAdvertiserId: string;
  geoTargetIds: number[];
  createdAt?: string;
  updatedAt?: string;
};

export type PublicationListSet = {
  id: string;
  name: string;
  brandgenAdvertiserId: string;
  publicationListIds: string[];
  createdAt?: string;
  updatedAt?: string;
};

export type RoleSet = {
  role: string;
  roleId: string;
  parentRoleId?: string | null;
  cookieSets: CookieSet[];
  children?: RoleSet[];
};

export type RoleTitleGroup = {
  roleType: string;
  roleTypeId: string;
  roleSet: RoleSet[];
};

export type GeoTargetOption = {
  country_code_2: string;
  country_code_3: string;
  key: string;
  region_code: string;
  region_name: string;
};

export type GeoState = {
  id: number;
  name: string;
};

export type GeoCountry = {
  id: number;
  name: string;
  states: GeoState[];
};

export type GeoTargetOptionsData = {
  "United States": GeoCountry;
  Canada: GeoCountry;
};

// export type Domain = {
//   active: boolean;
//   beeswaxListId: number;
//   beeswaxListItemId: number;
//   brandgenItemId: string;
//   createdAt: string;
//   domain: string;
//   domainListId: string;
//   updatedAt: string;
//   include?: boolean;
//   cpm?: number;
// };

// export enum ListType {
//   SINGLE = "single",
//   SPLIT = "split",
// }

// export type PublicationListGroup = {
//   id: string;
//   name: string;
//   listType: ListType;
//   domainLists: PublicDomainList[];
//   createdAt?: string;
//   updatedAt?: string;
// };

// export type DomainListGroup = {
//   id: string;
//   brandgenAdvertiserId: string;
//   name: string;
//   listType: ListType;
//   domainLists: CustomDomainList[];
//   createdAt?: string;
//   updatedAt?: string;
// };

// export type PublicDomainListData = {
//   beeswaxListId: number;
//   domain: string;
//   cpm: number;
// };

// export type DomainListData = {
//   beeswaxListId: number;
//   domain: string;
//   cpm: number;
// };

// export type PublicDomainList = {
//   beeswaxListId: number;
//   createdAt: string;
//   domainListId: string;
//   name: string;
//   publicPublications: Domain[];
// };

// export type CustomDomainList = {
//   beeswaxListId: number;
//   brandgenAdvertiserId: string;
//   createdAt: string;
//   domainListId: string;
//   name: string;
//   updatedAt: string;
//   domains: Domain[];
//   groupId: string;
// };

export type BrandgenPublicationList = {
  id: string;
  name: string;
  brandgenAdvertiserId: string | null;
  adLibAdvertiserId: number | null;
  listDomains?: Array<BrandgenListDomain>;
  createdAt?: string;
  updatedAt?: string;
};

export type BrandgenListDomain = {
  id: string;
  name: string;
  listId: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type PublicationListCreateData = {
  id: string;
  name: string;
  adLibRuleGroupId: number;
  brandgenAdvertiserId: string;
  adLibAdvertiserId?: number | null;
};

export type BeeswaxCampaignImage = {
  creative_asset_name: string;
  height: string;
  width: string;
  creative_asset_id: number;
  displayAdUrl: string;
};

export type CookieState = Record<string, CookieSet[]>;

export type CampaignStepperSlideType = {
  component: JSX.Element;
  title: string;
  reduxAction: (...args: any) => void;
  icon?: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
  name: string;
};

export type EmailsToHash = {
  domain: string;
  emails: string[];
};

export type FoundEmailsInfo = {
  fromFile: string | null;
  emailsToHash: Array<EmailsToHash>;
};

export enum CampaignPageTypes {
  landingPage = "landing_page",
  websitePage = "site_page",
  blogPosts = "blogPosts",
}

export enum CampaignMediaTypeEnum {
  VIDEO = "video",
  DISPLAY = "display",
}

export const CUSTOM_SLIDE_NAMES = {
  KEYWORD: "keywords",
  GEOTARGET: "geographyTarget",
  ZIP_CODE_TARGET: "zipCodeTarget",
  DOMAIN_LIST: "domainList",
  CUSTOM_COMPANIES: "customCompanies",
  CUSTOM_ROLETITLE: "customRoleTitle",
};

export const MANAGED_SLIDE_NAMES = {
  COMPANIES: "companies",
  TITLES: "titles",
};

export const DEFAULT_SLIDE_NAMES = {
  ADVERTISER_SELECT: "advertiserSelect",
  BASIC_DETAILS: "basicDetails",
  ...MANAGED_SLIDE_NAMES,
  PAGES: "pages",
  AD_GROUPS: "adGroups",
  SUMMARY: "summary",
  PIXEL: "pixel",
};

export enum CAMPAIGN_STEPS {
  SELECT_ADVERTISER = "selectAdvertiser",
  CAMPAIGN_TEMPLATE_DETAILS = "campaignTemplateDetails",
  CAMPAIGN_DETAILS = "campaignDetails",
  LANDING_PAGE = "landingPage",
  AD_GROUP_PAGE = "adGroupPage",
  SUMMURY_PAGE = "summaryPage",
  ROLE_TITLES = "roleTitles",
  TARGET_COMPANIES = "targetCompanies",
  PUBLICATION_LISTS = "publicationLists",
  ZIP_CODES = "zipCodes",
  STATE_PROVINCES = "stateProvinces",
  VISITORS_RETARGETING_SEGMENTS = "visitorsRetargetingSegments",
  CRM_RETARGETING_SEGMENTS = "crmRetargetingSegments",
}

export const mapAdGroupTemplateToAdGroupToCreate = (template: any): AdGroupToCreateType => {
  return {
    name: template.name,
    description: template.description,
    adGroupType: template.adGroupType,
    creativeAssets: template.creativeTemplates.map((ct: any) => ({
      height: ct.height,
      width: ct.width,
      fileType: template.adGroupType,
      duration: ct.duration,
      sourceUrl: ct.creativeTemplateSrc,
      name: ct.name,
      size: ct.size_in_bytes,
      mimetype: ct.creativeType,
    })),
  };
};

export type CampaignTemplate = {
  id: string;
  selectedAdvertiser: string;
  templateName: string;
  templateSteps: CAMPAIGN_STEPS[];
  selectedAdGroupTemplate: string;
  showInHubspot: boolean;
  maxCompanies: number;
  agencyId: string;
  createdBy: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  maxBid: number;
  dailyBudget: number;
  estimatedBudget: number;
  isVideoCampaign: boolean;
  landingPage: string;
  foundCompanyInfo: string[] | null;
  selectedGeoTargetOptions: string[] | null;
  selectedZipCodes: string[] | null;
  selectedSegmentIds: string[] | null;
  selectedDomainListsIds: string[] | null;
  selectedDomainIds: string[] | null;
  selectedDomainNames: string[] | null;
  foundEmailsInfo: string[] | null;
  campaignType?: string | null;
};
